import { alpha, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const rtl = { direction: "rtl" as const, textAlign: "right" as const };

const chassisRowGrid = {
  display: "grid",
  gridTemplateColumns: "56px minmax(0, 1fr) auto",
  gap: "24px",
  width: "100%",
  alignItems: "end",
  direction: "rtl",
} as const;

const finalResultsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: "12px",
  width: "100%",
  alignItems: "center",
  direction: "rtl",
} as const;

const panelCard = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "16px",
  width: "100%",
  borderRadius: "4px",
  padding: "16px",
  boxSizing: "border-box" as const,
  ...rtl,
};

const fieldCell = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "2px",
  minWidth: 0,
  alignItems: "flex-start" as const,
  ...rtl,
};

const label16Cap = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: "16px",
  fontWeight: 500,
  fontStyle: "normal" as const,
  lineHeight: "140%",
  letterSpacing: 0,
  textTransform: "capitalize" as const,
});

const hebrewNewText = (
  theme: Theme,
  size: string,
  weight: number,
  extra: Record<string, unknown> = {},
) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: size,
  fontWeight: weight,
  fontStyle: "normal" as const,
  lineHeight: "140%",
  letterSpacing: 0,
  color: theme.palette.secondary.main,
  ...rtl,
  ...extra,
});

const statusText = (theme: Theme, size: string, weight: number) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: size,
  fontWeight: weight,
  fontStyle: "normal" as const,
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "center" as const,
  width: "100%",
  color: "inherit" as const,
});

const tonalBorder = (color: string, hoverAlpha = 0.14, bgAlpha = 0.08) => ({
  borderColor: color,
  color,
  backgroundColor: alpha(color, bgAlpha),
  "&:hover": { backgroundColor: alpha(color, hoverAlpha) },
});

const outlineBtn = (theme: Theme, color: string) => ({
  height: 43,
  minHeight: 43,
  boxSizing: "border-box" as const,
  borderRadius: "4px",
  border: `1px solid ${color}`,
  color,
  textTransform: "none" as const,
  fontFamily: theme.typography.fontFamily,
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: "140%",
  padding: "10px 16px",
  whiteSpace: "nowrap" as const,
  "&:hover": {
    borderColor: color,
    backgroundColor: alpha(color, 0.06),
  },
});

const rowBox = (theme: Theme) => ({
  height: 67,
  minHeight: 67,
  boxSizing: "border-box" as const,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  padding: "12px 16px 12px 8px",
  backgroundColor: theme.palette.background.paper,
});

const fieldLabel = (theme: Theme) =>
  hebrewNewText(theme, "12px", 500, {
    color: theme.palette.text.primary,
    verticalAlign: "middle",
    margin: 0,
    padding: 0,
    flexShrink: 0,
    width: "100%",
  });

const chassisCellValue = (theme: Theme) => ({
  display: "flex",
  alignItems: "center",
  height: 30,
  width: "100%",
  ...hebrewNewText(theme, "14px", 400, { verticalAlign: "middle" }),
});

const chassisDescriptionValueStyle = (theme: Theme) => ({
  ...chassisCellValue(theme),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const bodyText = (theme: Theme, weight = 400) => hebrewNewText(theme, "14px", weight);

const finalResultsDataCell = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 400,
  fontStyle: "normal" as const,
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: theme.palette.secondary.main,
  ...rtl,
});

const inputBox70 = (theme: Theme) => ({
  width: 70,
  minWidth: 70,
  height: 30,
  boxSizing: "border-box" as const,
  borderRadius: "4px",
  padding: "10px 8px",
  fontFamily: theme.typography.fontFamily,
  fontSize: "14px",
  lineHeight: "1",
  ...rtl,
});

export const useStyles = makeStyles((theme: Theme) => {
  const danger = theme.palette.centreOfGravity.danger;
  const dividerLine = { width: "100%", height: 1, backgroundColor: theme.palette.divider };
  const iconGlyph = (size: string) => ({
    fontSize: size,
    fontWeight: 400,
    lineHeight: 1,
    fontFamily: theme.typography.fontFamily,
  });

  return {
    pageRoot: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      ...rtl,
    },

    // CentreOfGravityPanel
    panelSection: {
      ...panelCard,
      backgroundColor: theme.palette.background.paper,
      minWidth: 0,
      maxWidth: "100%",
      overflow: "hidden",
    },
    divider: dividerLine,
    dataRow: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr) auto",
      gap: "16px",
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      height: 57,
      minHeight: 57,
      boxSizing: "border-box",
      alignItems: "center",
      ...rtl,
    },
    metricGroup: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: "16px",
      minWidth: 0,
      width: "100%",
      ...rtl,
    },
    dataRowEndGroup: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: "16px",
      flexShrink: 0,
      minWidth: "max-content",
    },
    rowTitle: {
      fontFamily: theme.typography.fontFamily,
      fontSize: "16px",
      fontWeight: 600,
      fontStyle: "normal",
      lineHeight: "140%",
      letterSpacing: 0,
      textAlign: "right",
      textTransform: "capitalize",
      color: theme.palette.secondary.main,
      direction: "rtl",
      alignSelf: "center",
      width: "auto",
      whiteSpace: "nowrap",
    },
    metricField: { ...fieldCell, gap: "10px", width: "100%", minWidth: 0, overflow: "hidden" },
    metricLabel: {
      ...fieldLabel(theme),
      fontSize: "14px",
      fontWeight: 400,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    metricValue: { ...bodyText(theme), margin: 0, padding: 0, width: "100%", minWidth: 0 },
    actionButtons: { display: "flex", gap: "16px", alignItems: "center", flexShrink: 0 },
    actionBtn: {
      ...label16Cap(theme),
      height: 48,
      minHeight: 48,
      padding: "12px 16px 12px 8px",
      borderRadius: "4px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      boxSizing: "border-box",
    },
    statusBlock: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "2px",
      height: 48,
      minHeight: 48,
      width: 148,
      minWidth: 148,
      boxSizing: "border-box",
      borderRadius: "4px",
      padding: "8px 12px",
    },
    statusOk: { backgroundColor: theme.palette.success.main, color: theme.palette.common.white },
    statusNotOk: { backgroundColor: danger, color: theme.palette.common.white },
    statusLabel: statusText(theme, "10px", 400),
    statusValue: statusText(theme, "14px", 600),
    optionRow: { display: "flex", gap: "10px", justifyContent: "flex-end", width: "100%", ...rtl },
    optionCard: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "10px",
      height: 57,
      minHeight: 57,
      boxSizing: "border-box",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "4px",
      backgroundColor: theme.palette.background.paper,
      padding: "10px 16px",
      cursor: "pointer",
      flexShrink: 0,
      ...rtl,
      "& .MuiRadio-root": { padding: 0, flexShrink: 0 },
    },
    optionCardSelected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    optionLabel: { ...label16Cap(theme), color: theme.palette.secondary.main, whiteSpace: "nowrap", ...rtl },
    adjustRow: { display: "flex", gap: "19px", justifyContent: "flex-end", width: "100%", ...rtl },
    adjustButton: {
      width: 57,
      minWidth: 57,
      height: 57,
      minHeight: 57,
      borderRadius: "7.6px",
      boxSizing: "border-box",
      padding: 0,
      borderWidth: 1.9,
      borderStyle: "solid",
    },
    adjustButtonPlus: tonalBorder(theme.palette.success.main),
    adjustButtonMinus: tonalBorder(danger),
    adjustIcon: iconGlyph("24px"),
    resetRow: { display: "flex", justifyContent: "flex-end", width: "100%", ...rtl },
    resetButton: {
      ...label16Cap(theme),
      width: "100%",
      height: 57,
      minHeight: 57,
      padding: "10px 16px",
      borderRadius: "4px",
      border: `1px solid ${theme.palette.text.primary}`,
      color: theme.palette.text.primary,
      boxSizing: "border-box",
      "&.MuiButton-outlined": { borderColor: theme.palette.text.primary, color: theme.palette.text.primary },
      "&:hover": {
        borderColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
        backgroundColor: alpha(theme.palette.text.primary, 0.04),
      },
    },

    // Data row panels (Chassis / Vehicle / Additional)
    panelSectionTitle: {
      fontFamily: theme.typography.fontFamily,
      fontSize: "16px",
      fontWeight: 600,
      fontStyle: "normal" as const,
      lineHeight: "140%",
      letterSpacing: 0,
      textTransform: "capitalize" as const,
      color: theme.palette.secondary.main,
      width: "100%",
      whiteSpace: "nowrap",
      ...rtl,
    },
    chassisTable: { display: "flex", flexDirection: "column", gap: "24px", width: "100%", ...rtl },
    chassisDataRow: { ...chassisRowGrid, ...rowBox(theme), alignItems: "end" },
    chassisSymbolValue: chassisCellValue(theme),
    chassisDescriptionValue: chassisDescriptionValueStyle(theme),
    chassisFieldCell: fieldCell,
    chassisFieldLabel: fieldLabel(theme),
    chassisInputGroup: { display: "flex", gap: "10px", ...rtl, alignItems: "flex-end", flexShrink: 0 },
    chassisInputFieldCell: { ...fieldCell, width: 70, minWidth: 70 },
    chassisValueInput: {
      width: 70,
      minWidth: 70,
      margin: 0,
      direction: "ltr",
      "& .MuiOutlinedInput-root": {
        ...inputBox70(theme),
        direction: "ltr",
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.background.paper,
        "& fieldset": { borderColor: theme.palette.divider, borderWidth: "1px" },
        "&:hover fieldset": { borderColor: theme.palette.divider },
        "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main, borderWidth: "1px" },
      },
      "& .MuiOutlinedInput-input": { padding: 0, boxSizing: "border-box", ...rtl, height: "auto" },
    },
    chassisCalculationValue: {
      ...inputBox70(theme),
      fontWeight: 500,
      color: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
    },

    // VehicleMeasuresPanel (G row adjuster)
    vehicleMeasureDescriptionWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "8px",
      height: 30,
      width: "100%",
      ...rtl,
    },
    vehicleMeasureDescriptionText: {
      ...chassisDescriptionValueStyle(theme),
      width: "auto",
      flex: "0 1 auto",
    },
    vehicleMeasureInlineAdjuster: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 },
    vehicleMeasureInlineNote: { ...bodyText(theme), fontFamily: theme.typography.fontFamily, whiteSpace: "nowrap" },
    vehicleMeasureInlineButton: {
      width: 32,
      height: 32,
      minWidth: 32,
      minHeight: 32,
      borderRadius: "4px",
      boxSizing: "border-box",
      padding: 0,
      border: "1px solid",
    },
    vehicleMeasureInlineButtonPlus: tonalBorder(theme.palette.success.main),
    vehicleMeasureInlineButtonMinus: tonalBorder(danger),
    vehicleMeasureInlineIcon: iconGlyph("20px"),

    // FinalResultsPanel
    finalResultsTable: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "4px",
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    finalResultsHeaderRow: {
      ...finalResultsGrid,
      height: 52,
      minHeight: 52,
      maxHeight: 52,
      padding: "0 16px",
      boxSizing: "border-box",
      border: `1px solid ${theme.palette.divider}`,
      borderTopLeftRadius: "4px",
      borderTopRightRadius: "4px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[400],
    },
    finalResultsHeaderCell: {
      ...fieldLabel(theme),
      whiteSpace: "nowrap",
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      height: "100%",
    },
    finalResultsBodyRow: { ...finalResultsGrid, padding: "12px 16px", boxSizing: "border-box" },
    finalResultsValueCell: finalResultsDataCell(theme),
    finalResultsActionsBox: {
      ...rowBox(theme),
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: "24px",
      width: "100%",
      ...rtl,
    },
    finalResultsControls: { display: "flex", alignItems: "flex-end", gap: "10px", ...rtl, flexShrink: 0 },
    finalResultsDangerBtn: outlineBtn(theme, danger),
    finalResultsSuccessBtn: outlineBtn(theme, theme.palette.success.main),
    finalResultsHint: {
      ...bodyText(theme, 600),
      flex: 1,
      minWidth: 0,
      height: 30,
      display: "flex",
      alignItems: "center",
    },
  };
});
